// oxlint-disable no-explicit-any

/**
 * BIOMETRIC WEBVIEW COMMUNICATION LAYER
 * =====================================
 *
 * This file handles biometric operations for the WebView context in matrix-native app.
 *
 * ARCHITECTURE OVERVIEW:
 * ----------------------
 * In matrix-native app, the AuthFlow runs inside a React Native WebView component. This creates
 * a unique challenge where biometric operations need to be performed by the native React Native
 * layer (which has access to device hardware), while the UI and business logic runs in the WebView.
 *
 * COMMUNICATION FLOW:
 * ------------------
 * 1. WebView (this file) ──────────────────► React Native (biometric.native.ts)
 * 2. React Native performs actual biometric operations using react-native-biometrics
 * 3. React Native ──────────────────────────► WebView (this file) with results
 *
 * IMPLEMENTATION DETAILS:
 * ----------------------
 * - Uses window.ReactNativeWebView.postMessage() to send messages to native layer
 * - Listens for responses via window.addEventListener('message', ...)
 * - Each request has a unique requestId to match responses to requests
 * - Implements 60-second timeout for all operations
 * - Supports bidirectional communication with success/error handling
 *
 * NATIVE COUNTERPART:
 * ------------------
 * The corresponding native implementation is in:
 * - packages/carbon/src/.../biometric.native.ts (actual biometric operations)
 *
 * The legacy WebView message bridge was removed once ua-governance-native
 * took over auth flows as real InterfacePage routes — biometric is now
 * invoked directly by the native page's no-code action chain.
 *
 * MESSAGE TYPES:
 * -------------
 * Request types: biometric_enroll, biometric_revoke, biometric_verify
 * Success types: biometric_enroll_success, biometric_revoke_success, biometric_verify_success
 * Response type: biometric_response (with success/error fields)
 *
 * TEAM KNOWLEDGE:
 * --------------
 * For future development, anyone working on this should understand:
 * 1. WebView <-> Native communication is asynchronous and can fail
 * 2. Always include timeout handling for WebView operations
 * 3. Use unique requestIds to match request-response pairs
 * 4. Test biometric flows on both iOS and Android devices
 * 5. Consider fallback strategies when native functionality is unavailable
 */

import { UAApps } from '@unifyapps/defs/types/apps';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import {
  useDeregisterBiometric,
  useRegisterBiometric,
} from '@unifyapps/network/generated/user-rest-api/user-rest-api';
import { executeAuthWorkflowNode } from '../../../../../../components/DataSourceController/components/DataSourceWorkflowNode/api';
import {
  isReactNativeWebView,
  sendMessageToReactNativeSDK,
} from '../../utils/sendMessageToReactNativeSDK';
import { BiometricMessageTypes } from '../../utils/types';

export async function getBiometricInfo() {
  if (isReactNativeWebView()) {
    return await sendMessageToReactNativeSDK<
      BiometricMessageTypes,
      { available: boolean; biometryType: string }
    >(BiometricMessageTypes.INFO);
  }

  return { available: false, biometryType: '' };
}

/**
 * BIOMETRIC ENROLLMENT HOOK
 * =========================
 * Handles biometric enrollment (setting up fingerprint/face authentication).
 *
 * WEBVIEW FLOW:
 * 1. Send 'biometric_enroll' message to native
 * 2. Native prompts user for biometric authentication
 * 3. Native generates cryptographic key pair using react-native-biometrics
 * 4. Native returns public key and device identifier
 * 5. WebView registers public key with backend API
 * 6. Send 'biometric_enroll_success' confirmation to native
 *
 * NOTE: The 'success' message is important for native to update local state
 * (e.g., setIsBiometricIDPEnabled(true) in biometric.native.ts)
 */
export function useEnrollBiometrics() {
  const { mutateAsync } = useRegisterBiometric();

  const enrollViaReactNativeWebView = useEventCallback(async () => {
    // Step 1: Request native to perform biometric enrollment
    const response = await sendMessageToReactNativeSDK<
      BiometricMessageTypes,
      { publicKey: string; deviceIdentifierId: string }
    >(BiometricMessageTypes.ENROLL);

    // Step 2: Register the public key with backend
    await mutateAsync({
      data: {
        deviceIdentifierId: response.deviceIdentifierId,
        publicKey: response.publicKey,
      },
    });

    // Step 3: Notify native that backend registration was successful
    // This allows native to update its local storage (setIsBiometricIDPEnabled)
    await sendMessageToReactNativeSDK<BiometricMessageTypes>(BiometricMessageTypes.ENROLL_SUCCESS);
    return;
  });

  const enroll = useEventCallback(async () => {
    if (isReactNativeWebView()) {
      return await enrollViaReactNativeWebView();
    }

    // TODO: implement biometric enrollment in web (non-webview)
    // This would use Web Authentication API (WebAuthn) for browsers
    throw new Error('Biometric enrollment not available in web context');
  });

  return { enroll };
}

/**
 * BIOMETRIC REVOCATION HOOK
 * =========================
 * Handles removing biometric authentication (disabling fingerprint/face auth).
 *
 * WEBVIEW FLOW:
 * 1. Send 'biometric_revoke' message to native
 * 2. Native deletes cryptographic keys from secure storage
 * 3. Native returns device identifier for backend deregistration
 * 4. WebView deregisters device from backend API
 * 5. Send 'biometric_revoke_success' confirmation to native
 *
 * SECURITY NOTE: This permanently removes the user's biometric keys.
 * They will need to re-enroll if they want to use biometrics again.
 */
export function useRevokeBiometrics() {
  const { mutateAsync } = useDeregisterBiometric();

  const revokeViaReactNativeWebView = useEventCallback(async () => {
    // Step 1: Request native to revoke biometric keys
    const response = await sendMessageToReactNativeSDK<
      BiometricMessageTypes,
      { deviceIdentifierId: string }
    >(BiometricMessageTypes.REVOKE);

    // Step 2: Deregister device from backend
    await mutateAsync({ deviceIdentifierId: response.deviceIdentifierId });

    // Step 3: Notify native that backend deregistration was successful
    // This allows native to update its local storage (setIsBiometricIDPEnabled(false))
    await sendMessageToReactNativeSDK<BiometricMessageTypes>(BiometricMessageTypes.REVOKE_SUCCESS);
    return;
  });

  const revoke = useEventCallback(async () => {
    if (isReactNativeWebView()) {
      return await revokeViaReactNativeWebView();
    }

    // TODO: implement biometric revocation in web (non-webview)
    // This would use Web Authentication API (WebAuthn) for browsers
    throw new Error('Biometric revocation not available in web context');
  });

  return { revoke };
}

/**
 * BIOMETRIC VERIFICATION HOOK
 * ===========================
 * Handles biometric authentication for login (fingerprint/face verification).
 *
 * WEBVIEW FLOW:
 * 1. Send 'biometric_verify' message to native
 * 2. Native prompts user for biometric authentication
 * 3. Native creates cryptographic signature using stored private key
 * 4. Native returns username, device ID, and signature
 * 5. WebView sends signature to auth workflow for verification
 * 6. Backend validates signature against stored public key
 * 7. If valid, backend returns authentication tokens/redirect
 * 8. Send 'biometric_verify_success' confirmation to native
 *
 * SECURITY: The private key never leaves the device's secure enclave.
 * Only the signature is transmitted for verification.
 */
export function useVerifyBiometrics() {
  const verifyViaReactNativeWebView = useEventCallback(async () => {
    // Step 1: Request native to perform biometric verification
    const response = await sendMessageToReactNativeSDK<
      BiometricMessageTypes,
      {
        username: string;
        deviceIdentifierId: string;
        signature: string; // Cryptographic signature created by native
      }
    >(BiometricMessageTypes.VERIFY);

    // Step 2: Send signature to auth workflow for backend verification
    const payload = await executeAuthWorkflowNode(
      {
        context: {
          appName: UAApps.AuthByUA,
          resourceName: 'auth_by_unifyapps_verify_biometric_details',
        },
        id: 'verifyBiometricDetails',
        inputs: {
          returnTo: '/',
          formData: {
            userBiometricDetails: {
              username: response.username,
              deviceIdentifierId: response.deviceIdentifierId,
              signature: response.signature,
            },
          },
        },
      },
      { name: 'verifyBiometricDetails' },
    );

    // Step 3: Notify native that verification was successful
    // This allows native to update auth state (checkAuth() in biometric.native.ts)
    await sendMessageToReactNativeSDK(BiometricMessageTypes.VERIFY_SUCCESS);
    return payload;
  });

  const verify = useEventCallback(async () => {
    if (isReactNativeWebView()) {
      return await verifyViaReactNativeWebView();
    }

    // TODO: implement biometric verification in web (non-webview)
    // This would use Web Authentication API (WebAuthn) for browsers
    throw new Error('Biometric verification not available in web context');
  });

  return { verify };
}
